import IconButton from '@material-ui/core/IconButton'
import { styled, makeStyles } from '@material-ui/core/styles'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import MenuIcon from '@material-ui/icons/MoreHorizOutlined'
import classNames from 'classnames'
import React, { useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'

import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import useSafeEffect from 'src/commons/hooks/useSafeEffect'
import theme from 'src/theme'

import CardHeader from './CardHeader'

export interface ICardTabEntry {
  disabled?: boolean
  id: number
  label: string
  to?: string
}

const CustomTabs = styled(Tabs)({
  minHeight: '24px',
  height: '24px',
})

const CustomTab = styled(Tab)({
  height: '24px',
  minHeight: '24px',
  minWidth: 'max-content',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '24px',
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  letterSpacing: '0.15px',
  textTransform: 'uppercase',
  boxSizing: 'border-box',
  borderLeft: 'solid 1px #00000099',
  padding: '0px 12px 0px 12px',
  '&.Mui-selected': {
    fontWeight: '600',
  },
  '&:nth-child(1)': {
    paddingLeft: 0,
    border: 'none',
  },
})

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  title: {
    flex: 1,
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.palette.primary.main || '#0e85ff',
    padding: 20,
  },
  menuIcon: {
    fontSize: 30,
    cursor: 'pointer',
    color: '#192233',
  },
  button: {
    marginRight: 20,
  },
  content: {},
  centeredColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

interface ICardTabHeaderProps {
  className?: string
  entries: ICardTabEntry[]
  onMenuClick?: (event: any) => void
  onTabChange?: any
  showMenu?: boolean
  showSeparator?: boolean
}

function CardTabHeader(props: ICardTabHeaderProps) {
  const { entries = [], showMenu = false } = props
  const classes = useStyles()

  const history = useHistory()
  const location = useLocation()

  function tabIdFromPath() {
    const tabMatchingPathname = entries.find((tab) => tab.to === location.pathname)
    if (tabMatchingPathname) {
      return tabMatchingPathname.id
    }
    return false
  }
  const [currentTabId, setCurrentTabId] = useState<any>(tabIdFromPath())

  useSafeEffect(() => {
    if (currentTabId !== false) {
      const newTab = entries.find((t) => t.id === currentTabId)
      if (newTab && newTab.to !== location.pathname) {
        history.push({
          pathname: newTab.to,
          /*
          REMOVED 27/07/2023
          Passing parameters from a tab to another was causing issues if two
          filters with the same name on both tabs but different entries exists,
          it was trying to set the value of the new tab with the values of the precedent
          */
          // FIXME: 28/07/2023 ALM: Have to comment this line to avoid issue with filters between Tabs navigation
          // search: location.search,
        })
      }
    }
  }, [currentTabId])

  useDeepCompareEffect(() => {
    setCurrentTabId(tabIdFromPath())
  }, [location])

  function onClick(_: any, id: number) {
    const tab = entries.find((t) => t.id === id)
    if (tab) {
      setCurrentTabId(tab.id)
      if (props.onTabChange) {
        props.onTabChange(tab.id)
      }
    }
  }

  return (
    <CardHeader className={classNames(classes.root, props.className)}>
      <div className={classes.content}>
        <CustomTabs
          value={currentTabId}
          indicatorColor="primary"
          textColor="primary"
          onChange={onClick}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{
            style: {
              display: 'none',
            },
          }}
        >
          {entries.map((e) => {
            return (
              <CustomTab
                key={e.id}
                id={e.label.replaceAll(' ', '-')}
                value={e.id}
                label={e.label}
                disabled={e.disabled || false}
              />
            )
          })}
        </CustomTabs>
        {showMenu && (
          <div className={classes.centeredColumn}>
            <IconButton onClick={props.onMenuClick} className={classes.button}>
              <MenuIcon className={classes.menuIcon} />
            </IconButton>
          </div>
        )}
      </div>
    </CardHeader>
  )
}

export default CardTabHeader
